import React, { useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose, faImage, faRotateLeft, faVideo } from "@fortawesome/pro-regular-svg-icons";
import { websiteBuilderArchiveMediaItem, websiteBuilderRestoreMediaItem } from "redux/actions/websiteBuilder/editor/websiteBuilderEditor";
import "./addMediaView.scss";
import { IWebsiteBuilderMediaItem } from "redux/reducers/models/websiteBuilder";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { WebsiteBuilderToolbarMenuView } from "../../WebsiteBuilderToolbarMenuView";
import { isWebsiteBuilderImageAllowedFileSubtype, isWebsiteBuilderVideoAllowedFileSubtype } from "pages/secure/facility/websiteBuilder/editor/utility/websiteBuilderAllowedMedia";
import { MediaDraggable } from "./MediaDraggable";
import { WebsiteBuilderMediaDropZone } from "pages/secure/facility/websiteBuilder/editor/components/mediaDropZone/WebsiteBuilderMediaDropZone";

type TMediaGalleryTabType = "media" | "stock" | "archived";

interface IMediaGalleryTab {
  label: string;
  type: TMediaGalleryTabType;
}

export function AddMediaView() {
  const dispatch = useAppDispatch();

  const { t } = useTranslation();

  const media = useAppSelector(store => store.websiteBuilderStore.editor.media);
  const stockMedia = useAppSelector(store => store.websiteBuilderStore.editor.stockMedia);

  const [activeMediaGalleryTabType, setActiveMediaGalleryTabType] = useState<TMediaGalleryTabType>("media");

  function isMediaAllowedFileSubtype(subtype: string) {
    return isWebsiteBuilderImageAllowedFileSubtype(subtype) || isWebsiteBuilderVideoAllowedFileSubtype(subtype);
  }

  const mediaGalleryTabs: IMediaGalleryTab[] = useMemo(() => [
    {
      label: t("Media"),
      type: "media",
    },
    {
      label: t("Stock"),
      type: "stock",
    },
    {
      label: t("Archived"),
      type: "archived",
    },
  ], []);

  const galleryMedia: IWebsiteBuilderMediaItem[] = useMemo(() => {
    return media
      ?.filter(mediaItem =>
        isMediaAllowedFileSubtype(mediaItem.subtype) &&
        mediaItem.archived_at == null,
      ) ?? [];
  }, [media]);

  const galleryStockMedia: IWebsiteBuilderMediaItem[] = useMemo(() => {
    return stockMedia
      ?.filter(mediaItem => isMediaAllowedFileSubtype(mediaItem.subtype)) ?? [];
  }, [stockMedia]);

  const galleryArchivedMedia: IWebsiteBuilderMediaItem[] = useMemo(() => {
    return media
      ?.filter(mediaItem =>
        isMediaAllowedFileSubtype(mediaItem.subtype) &&
        mediaItem.archived_at != null,
      ) ?? [];
  }, [media]);

  function archiveMedia(id: number) {
    void dispatch(websiteBuilderArchiveMediaItem(id, t));
  }

  function restoreMedia(id: number) {
    void dispatch(websiteBuilderRestoreMediaItem(id, t));
  }

  function renderMediaTypeIcon(mediaItem: IWebsiteBuilderMediaItem) {
    if (isWebsiteBuilderImageAllowedFileSubtype(mediaItem.subtype)) {
      return (
        <FontAwesomeIcon
          className="media-gallery-item-type-icon"
          icon={faImage}
        />
      );
    } else if (isWebsiteBuilderVideoAllowedFileSubtype(mediaItem.subtype)) {
      return (
        <FontAwesomeIcon
          className="media-gallery-item-type-icon"
          icon={faVideo}
        />
      );
    } else {
      return null;
    }
  }

  function renderMediaItem(mediaItem: IWebsiteBuilderMediaItem) {
    if (isWebsiteBuilderImageAllowedFileSubtype(mediaItem.subtype)) {
      return (
        <img
          className="media-gallery-item"
          src={mediaItem.source}
          draggable="false"
        />
      );
    } else if (isWebsiteBuilderVideoAllowedFileSubtype(mediaItem.subtype)) {
      return (
        <video
          className="media-gallery-item"
          src={mediaItem.source}
          preload="metadata"
          muted
          playsInline
          disablePictureInPicture
          draggable="false"
        />
      );
    } else {
      return null;
    }
  }

  function renderUploadMediaSection() {
    return (
      <WebsiteBuilderMediaDropZone
        theme="dark"
        allowImages
        allowVideos
      />
    );
  }

  function renderMediaGallery() {
    const itemContainerClassName = "media-gallery-item-container";

    switch (activeMediaGalleryTabType) {
      case "media": {
        return (
          <>
            {galleryMedia.map(mediaItem => {
              return (
                <div
                  key={mediaItem.id}
                  className={itemContainerClassName}
                >
                  <MediaDraggable mediaItem={mediaItem}>
                    {renderMediaItem(mediaItem)}
                  </MediaDraggable>
                  {renderMediaTypeIcon(mediaItem)}
                  <FontAwesomeIcon
                    className="media-gallery-item-action-icon"
                    icon={faClose}
                    onClick={() => archiveMedia(mediaItem.id)}
                  />
                </div>
              );
            })}
          </>
        );
      }
      case "stock": {
        return (
          <>
            {galleryStockMedia.map(mediaItem => {
              return (
                <div
                  key={mediaItem.id}
                  className={itemContainerClassName}
                >
                  <MediaDraggable mediaItem={mediaItem}>
                    {renderMediaItem(mediaItem)}
                  </MediaDraggable>
                  {renderMediaTypeIcon(mediaItem)}
                </div>
              );
            })}
          </>
        );
      }
      case "archived": {
        return (
          <>
            {galleryArchivedMedia.map(mediaItem => {
              return (
                <div
                  key={mediaItem.id}
                  className={itemContainerClassName}
                >
                  {renderMediaItem(mediaItem)}
                  {renderMediaTypeIcon(mediaItem)}
                  <FontAwesomeIcon
                    className="media-gallery-item-action-icon"
                    icon={faRotateLeft}
                    onClick={() => restoreMedia(mediaItem.id)}
                  />
                </div>
              );
            })}
          </>
        );
      }
      default: {
        return null;
      }
    }
  }

  function renderMediaGallerySection() {
    return (
      <div className="website-builder-media-gallery-section">
        <div className="media-gallery-tabs">
          {mediaGalleryTabs.map((tab, index) => (
            <div
              key={index}
              className={classNames("media-gallery-tab", {
                "media-gallery-tab-active": tab.type === activeMediaGalleryTabType,
              })}
              onClick={() => setActiveMediaGalleryTabType(tab.type)}
            >
              {tab.label}
            </div>
          ))}
        </div>
        {renderMediaGallery()}
      </div>
    );
  }

  return (
    <WebsiteBuilderToolbarMenuView
      title={t("Add media")}
      sections={[
        {
          title: t("Upload media"),
          content: renderUploadMediaSection(),
        },
        {
          content: renderMediaGallerySection(),
        },
      ]}
    />
  );
}
